/**
 * AccommodationDetails Component
 * 
 * A comprehensive React component for displaying and managing accommodation details within a trip.
 * This component serves as the main view for individual accommodation entries, supporting both
 * viewing and editing modes, along with type-specific rendering and notes management.
 * 
 * Features:
 * - Dynamic accommodation type rendering
 * - Real-time editing capabilities
 * - Notes management system
 * - Last edited timestamp tracking
 * - Error handling and loading states
 * 
 * URL Parameters:
 * - tripId: The ID of the parent trip
 * - accommodationId: The ID of the specific accommodation entry
 * 
 * Authentication:
 * - Requires authenticated user (via AuthContext)
 * - User-specific data isolation
 * 
 * @component
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, useTrip, useUnsavedChanges, Notes, eventBridgeService, EVENT_TYPES, getPlaceById, useDays, RelatedDocumentsSection, EntityLinkerModal, LinkEntitiesButton, RelatedEntitiesSection, GenericConfirmModal, entityLinkingService, EntityDetailLayout, connectionRegistryService } from '@trip-planner-monorepo/shared';
import { accommodationService } from '../../services/accommodationService';
import AddAccommodation from '../AddAccommodation/AddAccommodation';
import { getTypeComponent } from './types';
import { RelatedDaysSection } from './common';
import './AccommodationDetails.css';
import ResourceModal from '@trip-planner-monorepo/shared/components/ResourceModal/ResourceModal';
import Modal from 'react-modal';
import UploadModal from '@trip-planner-monorepo/documents/components/UploadModal';
import { FaEdit, FaFile, FaHotel, FaHome, FaBuilding, FaBed } from 'react-icons/fa';


/**
 * Maps accommodation types to their corresponding icons
 */
const getTypeIcon = (type) => {
  switch (type?.toLowerCase()) {
  case 'hotel':
    return <FaHotel className="accommodation-type-icon" />;
  case 'house':
  case 'villa':
    return <FaHome className="accommodation-type-icon" />;
  case 'apartment':
  case 'condo':
    return <FaBuilding className="accommodation-type-icon" />;
  default:
    return <FaBed className="accommodation-type-icon" />;
  }
};

function AccommodationDetails() {
  // State Management
  const [accommodation, setAccommodation] = useState(null);  // Stores the current accommodation data
  const [loading, setLoading] = useState(true);             // Controls loading state display
  const [error, setError] = useState('');                   // Stores error messages
  const [isEditing, setIsEditing] = useState(false);        // Controls edit mode
  const [lastEditedTime, setLastEditedTime] = useState(null); // Tracks last modification time
  const [showCityChangeModal, setShowCityChangeModal] = useState(false);
  const [oldPlaceInfo, setOldPlaceInfo] = useState(null);
  const [placeData, setPlaceData] = useState(null);         // Stores linked place data
  const [linkedPlaceId, setLinkedPlaceId] = useState(null); // Registry-resolved place id (registry_only fallback)
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);

  // Unlink Modal State
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [entityToUnlink, setEntityToUnlink] = useState(null);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const isMounted = useRef(true);

  // Context and Router Hooks
  const { currentUser } = useAuth();                        // Current authenticated user
  const { effectiveUserId } = useTrip();                    // Effective user ID for Firestore operations
  const { tripId, accommodationId } = useParams();          // URL parameters
  const navigate = useNavigate();                           // Navigation helper

  // Fetch days data for the trip
  const { days } = useDays(tripId);

  /**
     * Data Fetching Effect
     * 
     * Fetches accommodation details when component mounts or dependencies change.
     * Includes safety checks for required data and error handling.
     * 
     * Dependencies:
     * - currentUser: Ensures user is authenticated
     * - tripId: Valid trip identifier
     * - accommodationId: Valid accommodation identifier
     */
  useEffect(() => {
    if (!currentUser || !tripId || !accommodationId) return;

    isMounted.current = true;
    setLoading(true);

    // Scroll to top when navigating to this page
    window.scrollTo(0, 0);

    fetchAccommodationDetails();

    // Subscribe to place linking events to refresh the data
    const placeLinkSubscription = eventBridgeService.subscribe(EVENT_TYPES.ACCOMMODATION_PLACE_LINKED, (eventData) => {
      // Only react if this suggestion is for this accommodation
      if (eventData.accommodationId === accommodationId && isMounted.current) {
        console.log('AccommodationDetails: This accommodation was linked to a place, refreshing data...');
        fetchAccommodationDetails();
      }
    });

    // Subscribe to suggestion acceptance to refresh the data
    const suggestionProcessedSubscription = eventBridgeService.subscribe(EVENT_TYPES.SUGGESTION_PROCESSED, (eventData) => {
      if ((eventData.status === 'accepted' || eventData.status === 'applied') &&
        eventData.data?.accommodationId === accommodationId && isMounted.current) {
        console.log('AccommodationDetails: Suggestion accepted/applied for this accommodation, refreshing data...');
        fetchAccommodationDetails();
      }
    });

    const linkingSubscription = eventBridgeService.subscribe(EVENT_TYPES.LINK_ACCOMMODATION_TO_PLACE, (eventData) => {
      // Only refresh if this is the accommodation that was linked
      if (eventData.accommodationId === accommodationId && isMounted.current) {
        console.log('AccommodationDetails: This accommodation was linked to a place, refreshing data...');
        fetchAccommodationDetails();
      }
    });

    return () => {
      isMounted.current = false;
      if (placeLinkSubscription && typeof placeLinkSubscription.unsubscribe === 'function') {
        placeLinkSubscription.unsubscribe();
      }
      if (suggestionProcessedSubscription && typeof suggestionProcessedSubscription.unsubscribe === 'function') {
        suggestionProcessedSubscription.unsubscribe();
      }
      if (linkingSubscription && typeof linkingSubscription.unsubscribe === 'function') {
        linkingSubscription.unsubscribe();
      }
    };
  }, [accommodationId, tripId, currentUser]);

  /**
   * Fetches accommodation details from the service
   * Optimized to fetch only the specific accommodation to prevent infinite loops
   */
  const fetchAccommodationDetails = async () => {
    try {
      // Use getAccommodationById which already includes content blocks
      const foundAccommodation = await accommodationService.getAccommodationById(effectiveUserId, tripId, accommodationId);

      if (!foundAccommodation) {
        throw new Error('Accommodation item not found');
      }

      // Debug logging for accommodation data issues
      console.log('🏨 [AccommodationDetails] Loaded accommodation:', {
        id: accommodationId,
        type: foundAccommodation.type,
        hasDetails: !!foundAccommodation.details,
        detailsKeys: foundAccommodation.details ? Object.keys(foundAccommodation.details) : [],
        summary: foundAccommodation.summary,
        hasContentBlocks: !!foundAccommodation.contentBlocks,
        contentBlocksCount: foundAccommodation.contentBlocks?.length || 0,
        fullAccommodation: foundAccommodation
      });

      setAccommodation(foundAccommodation);
      setLastEditedTime(foundAccommodation.updatedAt ? new Date(foundAccommodation.updatedAt) : null);

      // Fetch place data if accommodation is linked to a place. On registry_only/dual_write
      // trips the legacy placeReference may be absent, so resolve the located_in place from
      // the registry as a fallback.
      let resolvedPlaceId = foundAccommodation.placeReference?.placeId || null;
      if (!resolvedPlaceId) {
        try {
          const displayIndex = await connectionRegistryService.getRegistryDisplayIndex(effectiveUserId, tripId);
          resolvedPlaceId = displayIndex.accommodationPlaceId.get(foundAccommodation.id) || null;
        } catch (regError) {
          console.warn('AccommodationDetails: could not resolve place from registry', regError);
        }
      }
      setLinkedPlaceId(resolvedPlaceId);
      if (resolvedPlaceId) {
        try {
          const place = await getPlaceById(effectiveUserId, tripId, resolvedPlaceId);
          setPlaceData(place);
        } catch (placeError) {
          console.error('Error fetching linked place data:', placeError);
          setPlaceData(null);
        }
      } else {
        setPlaceData(null);
      }
    } catch (err) {
      setError('Failed to load accommodation details. Please try again.');
      console.error('Error fetching accommodation:', err);
    } finally {
      setLoading(false);
    }
  };


  /**
     * Handles saving updates to accommodation details
     * 
     * @param {Object} updatedAccommodationData - The new accommodation data to save
     * @returns {Promise<void>}
     * 
     * Process:
     * 1. Updates the accommodation in the backend
     * 2. Refreshes the local data
     * 3. Updates the last edited timestamp
     * 4. Exits edit mode
     */
  const handleSaveAccommodation = async (updatedAccommodationData) => {
    try {
      const result = await accommodationService.updateAccommodation(
        effectiveUserId,
        tripId,
        accommodationId,
        updatedAccommodationData
      );

      // Refresh only this specific accommodation data to avoid infinite loops
      await fetchAccommodationDetails();
      setIsEditing(false);

      // Show city change modal if needed
      if (result.cityChanged && result.oldPlaceReference?.placeId) {
        setOldPlaceInfo({
          city: result.oldCity,
          placeId: result.oldPlaceReference.placeId
        });
        setShowCityChangeModal(true);
      }
    } catch (err) {
      setError('Failed to update accommodation details. Please try again.');
      console.error('Error updating accommodation:', err);
    }
  };

  /**
     * Handles saving updates to accommodation notes
     * 
     * @param {string} content - The new notes content
     * @returns {Promise<void>}
     * 
     * Features:
     * - Preserves existing accommodation data
     * - Updates only the notes field
     * - Updates last edited timestamp
     * - Includes error handling
     */
  const handleSaveNotes = async (content) => {
    if (!accommodation) return;

    try {
      const updatedAccommodation = { ...accommodation, notes: content };
      await accommodationService.updateAccommodation(
        effectiveUserId,
        tripId,
        accommodationId,
        updatedAccommodation
      );

      setAccommodation(updatedAccommodation);
      setLastEditedTime(new Date());
    } catch (err) {
      setError('Failed to save notes. Please try again.');
      console.error('Error saving notes:', err);
    }
  };

  /**
   * Handles opening the document upload modal
   */
  const handleDocumentUpload = () => {
    setShowUploadModal(true);
  };

  /**
   * Handles document upload completion
   */
  const handleDocumentProcessed = async (result) => {
    console.log('Document processed from accommodation:', result);
    setShowUploadModal(false);
    // Refresh accommodation data to show the newly uploaded document
    await fetchAccommodationDetails();
  };

  /**
   * Handles entity link creation
   */
  const handleLinkCreated = async (links) => {
    console.log('Links created from accommodation:', links);
    // Refresh accommodation data to show the newly created links
    await fetchAccommodationDetails();
  };

  // Handle Unlink Request
  const handleUnlinkRequest = (entityType, entityId, entityName) => {
    setEntityToUnlink({ entityType, entityId, entityName });
    setUnlinkModalOpen(true);
  };

  // Handle Confirm Unlink
  const handleConfirmUnlink = async () => {
    if (!entityToUnlink) return;

    setIsUnlinking(true);
    try {
      await entityLinkingService.removeLink(
        effectiveUserId,
        tripId,
        'accommodation',
        accommodationId,
        entityToUnlink.entityType,
        entityToUnlink.entityId
      );

      // Refresh accommodation data
      await fetchAccommodationDetails();
      setUnlinkModalOpen(false);
      setEntityToUnlink(null);
    } catch (err) {
      console.error('Error unlinking entity:', err);
      setError('Failed to unlink entity. Please try again.');
    } finally {
      setIsUnlinking(false);
    }
  };

  // Conditional Rendering Handlers - Early returns handled by EntityDetailLayout
  if (!accommodation && !loading && !error) {
    return (
      <EntityDetailLayout
        className="accommodation-details"
        loading={false}
        error="Accommodation item not found."
      />
    );
  }

  // Get the type-specific component based on accommodation type
  const TypeSpecificComponent = accommodation ? getTypeComponent(accommodation.type) : null;

  // Determine display name
  const displayName = accommodation?.name || accommodation?.details?.name || 'Unnamed Accommodation';

  // Determine place display and subtitle. Use the registry-resolved place id as a fallback
  // for registry_only trips where the legacy placeReference is not written.
  const placeReference = accommodation?.placeReference;
  const effectivePlaceId = placeReference?.placeId || linkedPlaceId;
  const placeDisplayName = placeData ? (placeData.fullName || placeData.name || accommodation?.details?.city || 'Place') : (accommodation?.details?.city || 'Place');

  const subtitle = effectivePlaceId ? (
    <Link to={`/trips/${tripId}/places/${effectivePlaceId}`} className="place-link">
      {placeDisplayName}
    </Link>
  ) : (
    accommodation?.details?.city || null
  );

  // Title with type icon
  const title = (
    <>
      {accommodation && getTypeIcon(accommodation.type)}
      {displayName}
    </>
  );

  // Header Actions
  const titleAction = !isEditing && (
    <button
      className="edit-button inline"
      onClick={() => setIsEditing(true)}
      title="Edit accommodation"
    >
      <FaEdit />
    </button>
  );

  const primaryActions = !isEditing ? [
    <button
      key="upload"
      className="edit-button document-upload-button"
      onClick={handleDocumentUpload}
      title="Upload Document"
    >
      <FaFile /> Document Upload
    </button>
  ] : [];

  const secondaryActions = !isEditing ? [
    <LinkEntitiesButton
      key="link"
      className="edit-button"
      onClick={() => setShowLinkModal(true)}
    />
  ] : [];

  // Main Content - Type-specific details or edit form
  const mainContent = isEditing ? (
    <AddAccommodation
      isOpen={isEditing}
      onClose={() => setIsEditing(false)}
      onSave={handleSaveAccommodation}
      initialAccommodation={accommodation}
    />
  ) : (
    accommodation && TypeSpecificComponent && (
      <TypeSpecificComponent
        accommodation={accommodation}
        days={days}
        onUnlink={handleUnlinkRequest}
      />
    )
  );

  // Related Content
  const relatedContent = !isEditing && accommodation && (
    <div className="entity-connections">
      <RelatedDaysSection
        accommodation={accommodation}
        tripId={tripId}
        userId={effectiveUserId}
        days={days}
      />
      <RelatedEntitiesSection
        contentBlocks={accommodation.contentBlocks}
        tripId={tripId}
        userId={effectiveUserId}
        hostEntityType="accommodation"
        hostEntityId={accommodationId}
        excludeTypes={['document']}
        className="accommodation-related-entities"
        onUnlink={handleUnlinkRequest}
      />
      <RelatedDocumentsSection
        contentBlocks={accommodation.contentBlocks}
        tripId={tripId}
        userId={effectiveUserId}
        hostEntityType="accommodation"
        hostEntityId={accommodationId}
        className="accommodation-related-documents"
        onUnlink={handleUnlinkRequest}
      />
    </div>
  );

  // Notes Content
  const notesContent = !isEditing && accommodation && (
    <Notes
      content={accommodation.notes || ''}
      onSave={handleSaveNotes}
      lastEditedTime={lastEditedTime}
      className="accommodation-notes"
    />
  );

  /**
     * Main Component Render
     *
     * Uses EntityDetailLayout for consistent structure across all entity detail pages.
     *
     * Structure:
     * 1. EntityDetailLayout wrapper with slots
     * 2. Modals rendered as siblings (city change, upload, linker, unlink confirm)
     */
  return (
    <>
      <EntityDetailLayout
        className="accommodation-details"
        title={title}
        subtitle={subtitle}
        titleAction={titleAction}
        primaryActions={primaryActions}
        secondaryActions={secondaryActions}
        mainContent={mainContent}
        relatedContent={relatedContent}
        notesContent={notesContent}
        loading={loading}
        error={error}
      />

      {/* City change modal */}
      <Modal
        isOpen={showCityChangeModal}
        onRequestClose={() => setShowCityChangeModal(false)}
        contentLabel="Old Place Reminder"
        className="city-change-modal"
        overlayClassName="city-change-modal-overlay"
      >
        <div>
          <h2>Check Old Place Entry</h2>
          <p>
            You changed the city for this accommodation. You may want to check the old place entry for{' '}
            <Link to={`/trips/${tripId}/places/${oldPlaceInfo?.placeId}`}>{oldPlaceInfo?.city}</Link>
            {' '}to see if you still need it recorded in your trip.
          </p>
          <button onClick={() => setShowCityChangeModal(false)}>Dismiss</button>
        </div>
      </Modal>

      {/* Document Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onProcessed={handleDocumentProcessed}
          currentEntity={{
            type: 'accommodation',
            id: accommodationId,
            name: accommodation?.summary || accommodation?.name || 'Unnamed Accommodation'
          }}
        />
      )}

      {/* Entity Linker Modal */}
      {showLinkModal && accommodation && (
        <EntityLinkerModal
          sourceEntity={{
            type: 'accommodation',
            id: accommodationId,
            name: accommodation.summary || accommodation.name || 'Unnamed Accommodation'
          }}
          onLinkCreated={handleLinkCreated}
          onClose={() => setShowLinkModal(false)}
          allowMultiSelect={true}
        />
      )}

      {/* Unlink Confirmation Modal */}
      <GenericConfirmModal
        isOpen={unlinkModalOpen}
        onClose={() => setUnlinkModalOpen(false)}
        onConfirm={handleConfirmUnlink}
        title="Unlink Entity"
        message={
          entityToUnlink
            ? `Are you sure you want to unlink "${entityToUnlink.entityName}"? This will remove the link in both directions.`
            : 'Are you sure you want to unlink this entity?'
        }
        confirmText="Unlink"
        isDestructive={true}
        loading={isUnlinking}
      />
    </>
  );
}

export default AccommodationDetails;